import React, {
  ChangeEvent,
  ClipboardEvent,
  KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { MdClose } from "react-icons/md";

import { cx, focusInput, hasErrorInput } from "@/lib/tailwind-utils";

interface MultiTagInputProps {
  tags: string[];
  /**
   * Return `false` to signal the value was rejected — the input keeps its
   * current value so the user can fix it inline. Any other return (including
   * `undefined`/`void`) is treated as success and the input is cleared.
   */
  onAddTag: (tag: string) => boolean | void;
  onRemoveTag: (index: number) => void;
  /** Optional batch add — used for a pasted list so callers can apply all at once. */
  onAddTags?: (tags: string[]) => void;
  placeholder: string;
  hasError?: boolean;
  errorMessage?: string;
  className?: string;
  /** When true, pasting a comma/newline/slash-separated blob adds each token as a tag. */
  splitOnPaste?: boolean;
}

const MultiTagInput: React.FC<MultiTagInputProps> = ({
  tags,
  onAddTag,
  onRemoveTag,
  onAddTags,
  placeholder,
  hasError,
  errorMessage,
  className,
  splitOnPaste,
}) => {
  const [input, setInput] = useState<string>("");
  const [inputVisible, setInputVisible] = useState<boolean>(tags.length === 0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (tags.length === 0) {
      setInputVisible(true);
    }
  }, [tags]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === "Enter" || e.key === "," || e.key === "/") && input.trim()) {
      e.preventDefault();
      const accepted = onAddTag(input.trim());
      if (accepted !== false) setInput("");

      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    if (!splitOnPaste) return;
    const text = e.clipboardData.getData("text");
    // Only intercept multi-token pastes; single values paste normally.
    if (!/[,\n/]/.test(text)) return;
    e.preventDefault();
    const parsed = text
      .split(/[,\n/]+/)
      .map((t) => t.trim())
      .filter(Boolean);
    if (parsed.length === 0) return;
    // Prefer a single batch update so rapid per-tag calls don't clobber each other.
    if (onAddTags) {
      onAddTags(parsed);
    } else {
      parsed.forEach((t) => onAddTag(t));
    }
    setInput("");
  };

  const handleClick = () => {
    setInputVisible(true);
    setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
  };

  const handleBlur = () => {
    let keepInputOpen = false;
    if (input.trim()) {
      const accepted = onAddTag(input.trim());
      if (accepted !== false) {
        setInput("");
      } else {
        keepInputOpen = true;
      }
    }
    if (tags.length > 0 && !keepInputOpen) {
      setInputVisible(false);
    }
  };

  return (
    <>
      <div
        className={cx(
          "flex w-full flex-wrap items-center rounded-lg border p-1",
          className,
        )}
        onClick={handleClick}
      >
        {tags.map((tag, index) => (
          <span
            key={index}
            className="mx-1 my-0.5 flex min-h-7 max-w-[calc(100%-0.5rem)] items-center space-x-2 rounded-md bg-accent px-2 py-1 text-caption text-accent-foreground"
          >
            <span className="min-w-0 break-words">{tag}</span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRemoveTag(index);
              }}
              className="ml-1 shrink-0 text-accent-foreground/70 hover:text-accent-foreground"
            >
              <MdClose className="h-3.5 w-3.5" />
            </button>
          </span>
        ))}
        {inputVisible && (
          <input
            ref={inputRef}
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            className={cx(
              "my-[1px] flex-grow rounded-md border px-2 py-1 text-body shadow-none transition-all duration-200 ease-in-out placeholder:text-foreground-muted bg-transparent text-foreground",
              focusInput,
              tags.length ? "border-border-secondary" : "border-transparent",
              hasError ? hasErrorInput : "",
            )}
            placeholder={tags.length === 0 ? placeholder : ""}
            onBlur={handleBlur}
            style={tags.length ? { maxWidth: "100%" } : { width: "100%" }}
          />
        )}
      </div>

      {hasError && (
        <p className="mt-1.5 text-caption text-status-error-text">
          {errorMessage}
        </p>
      )}
    </>
  );
};

export default MultiTagInput;
